import { useState } from 'react';
import { createCardCheckoutSession, removeCard } from '../api/endpoints';
import type { CompanyResponse } from '../api/types';
import { Modal } from './Modal';
import { useToast } from './ToastProvider';

/**
 * The card on file. Lives on Billing (Credits) since 2026-10-23, next to the balance and auto
 * top-up that depend on it; it used to sit on My Company. Changing it is ClientAdmin only, and the
 * server enforces the same (CompanyController). Razor: the Payment Method card on Account/Credits.
 */
export function PaymentMethodCard({
  card,
  canManage,
  onChanged,
}: {
  card: CompanyResponse['card'];
  canManage: boolean;
  onChanged: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function handleUpdateCard() {
    setBusy(true);
    try {
      const result = await createCardCheckoutSession();
      if (result.success && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      showError(result.error ?? 'Could not start the card update.');
    } catch {
      showError('Could not start the card update.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveCard() {
    setBusy(true);
    try {
      const result = await removeCard();
      if (result.success) {
        showSuccess('Card removed. You can add a new card at any time.');
        onChanged();
      } else {
        showError(result.error ?? 'The card could not be removed.');
      }
    } catch {
      showError('The card could not be removed.');
    } finally {
      setBusy(false);
      setConfirmRemove(false);
    }
  }

  return (
    <div className="card h-100" id="payment-method">
      <div className="card-header d-flex align-items-center gap-2">
        <i className="bi bi-credit-card text-primary" />
        <span className="fw-semibold">Payment Method</span>
      </div>
      <div className="card-body">
        {card ? (
          <>
            <div className="d-flex align-items-center gap-3 mb-3">
              <i className={`bi ${cardBrandIcon(card.brand)} fs-3 text-body-secondary`} />
              <div>
                <div className="fw-semibold">
                  {card.brand} •••• {card.last4}
                </div>
                {card.expiryMonth && card.expiryYear && (
                  <div className="text-body-secondary small">
                    Expires {String(card.expiryMonth).padStart(2, '0')}/
                    {card.expiryYear}
                  </div>
                )}
              </div>
            </div>

            {canManage && (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleUpdateCard}
                  disabled={busy}
                >
                  <i className="bi bi-arrow-repeat me-1" />
                  Replace card
                </button>
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => setConfirmRemove(true)}
                  disabled={busy}
                >
                  <i className="bi bi-trash me-1" />
                  Remove
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-body-secondary small">
              No card on file. Verifications draw down prepaid credit, and without a card you
              cannot buy more or use auto top-up.
            </p>
            {canManage && (
              <button className="btn btn-primary btn-sm" onClick={handleUpdateCard} disabled={busy}>
                <i className="bi bi-credit-card me-1" />
                {busy ? 'Starting…' : 'Add a payment method'}
              </button>
            )}
          </>
        )}
        {!canManage && (
          <p className="text-body-secondary small mb-0 mt-2">Only a company admin can change the card.</p>
        )}
      </div>

      <Modal
        show={confirmRemove}
        title="Remove card?"
        onClose={() => setConfirmRemove(false)}
      >
        <p className="mb-3">
          Auto top-up will stop working and you will not be able to buy credit until you add a new
          card. Verifications continue while your balance lasts.
        </p>
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-secondary" onClick={() => setConfirmRemove(false)}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={handleRemoveCard} disabled={busy}>
            Remove card
          </button>
        </div>
      </Modal>
    </div>
  );
}


function cardBrandIcon(brand: string): string {
  switch (brand.toLowerCase()) {
    case 'visa':
      return 'bi-credit-card-2-front-fill';
    case 'mastercard':
      return 'bi-credit-card-fill';
    case 'amex':
      return 'bi-credit-card-2-back-fill';
    default:
      return 'bi-credit-card';
  }
}
